import { supabase } from './client'

export interface ReviewData {
  providerId: string
  appointmentId?: string
  rating: number
  comment?: string
  service?: string
}

export interface ReviewResult {
  success: boolean
  error?: string
}

// ── Internal helpers ─────────────────────────────────────────────────────────

async function getPatientId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return null
  const { data } = await supabase
    .from('patients')
    .select('id')
    .eq('auth_id', user.id)
    .single()
  return (data as any)?.id ?? null
}

// ── Service ──────────────────────────────────────────────────────────────────

export const reviewService = {
  /**
   * Check if the current patient has already reviewed a given appointment.
   */
  async hasReviewedAppointment(appointmentId: string): Promise<boolean> {
    try {
      const patientId = await getPatientId()
      if (!patientId) return false
      const { data } = await supabase
        .from('reviews')
        .select('id')
        .eq('appointment_id', appointmentId)
        .eq('patient_id', patientId)
        .maybeSingle()
      return !!data
    } catch {
      return false
    }
  },

  /**
   * Submit a review. Prevents duplicates by checking `appointment_id` uniqueness per patient.
   */
  async createReview(data: ReviewData): Promise<ReviewResult> {
    try {
      const patientId = await getPatientId()
      if (!patientId) return { success: false, error: 'Not authenticated' }

      // Duplicate guard
      if (data.appointmentId) {
        const already = await this.hasReviewedAppointment(data.appointmentId)
        if (already) {
          return { success: false, error: 'You have already reviewed this appointment.' }
        }
      }

      const { error } = await supabase.from('reviews').insert({
        provider_id: data.providerId,
        patient_id: patientId,
        appointment_id: data.appointmentId ?? null,
        rating: data.rating,
        comment: data.comment || null,
        service: data.service || null,
        verified: true,
      })

      if (error) {
        // Unique constraint on (appointment_id, patient_id)
        if (error.code === '23505') {
          return { success: false, error: 'You have already reviewed this appointment.' }
        }
        throw error
      }
      return { success: true }
    } catch (err: any) {
      return { success: false, error: err.message as string }
    }
  },
}
